import { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { Star } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";

interface ReviewFormProps {
  orderId: string;
  onSuccess: () => void;
}

export default function ReviewForm({ orderId, onSuccess }: ReviewFormProps) {
  const { toast } = useToast();
  const [rating, setRating] = useState(0);
  const [hoveredStar, setHoveredStar] = useState(0);
  const [title, setTitle] = useState("");
  const [body, setBody] = useState("");

  const submitMutation = useMutation({
    mutationFn: async () => {
      // Must go through apiRequest (not bare fetch) so the
      // marketlink:api event fires — that's what the MissionsEngine
      // listens to for auto-completing the "Leave a review" mission.
      const res = await apiRequest("POST", "/api/reviews", {
        orderId,
        rating,
        ...(title.trim() ? { title: title.trim() } : {}),
        ...(body.trim() ? { body: body.trim() } : {}),
      });
      return res.json();
    },
    onSuccess: () => {
      toast({ title: "Review submitted" });
      onSuccess();
    },
    onError: (err: Error) => {
      toast({ title: "Could not submit review", description: err.message, variant: "destructive" });
    },
  });

  const displayRating = hoveredStar || rating;

  return (
    <Card>
      <CardContent className="pt-6 space-y-4">
        <div className="text-sm font-semibold">Leave a review</div>

        {/* Star picker */}
        <div className="flex gap-1">
          {[1, 2, 3, 4, 5].map((star) => (
            <button
              key={star}
              type="button"
              className="p-0.5 focus:outline-none"
              onMouseEnter={() => setHoveredStar(star)}
              onMouseLeave={() => setHoveredStar(0)}
              onClick={() => setRating(star)}
              aria-label={`Rate ${star} star${star > 1 ? "s" : ""}`}
            >
              <Star
                className={`h-7 w-7 transition-colors ${
                  star <= displayRating
                    ? "fill-yellow-400 text-yellow-400"
                    : "text-muted-foreground"
                }`}
              />
            </button>
          ))}
        </div>

        <Input
          placeholder="Title (optional)"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          maxLength={120}
        />

        <Textarea
          placeholder="Share your experience (optional)"
          value={body}
          onChange={(e) => setBody(e.target.value)}
          rows={3}
          maxLength={2000}
        />

        <Button
          onClick={() => submitMutation.mutate()}
          disabled={rating === 0 || submitMutation.isPending}
          className="w-full"
        >
          {submitMutation.isPending ? "Submitting..." : "Submit Review"}
        </Button>
      </CardContent>
    </Card>
  );
}
